import { useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Sparkles, Lightbulb, MapPin } from 'lucide-react'
import { useTrip } from '../trips/useTrip'
import { usePlanTrip, useApplyPlan, useRecommendations } from './useAi'
import type { AiPlanPreview } from '../../shared/api/ai.http'
import { Button } from '../../shared/components/Button'
import { Spinner } from '../../shared/components/Spinner'
import { EmptyState } from '../../shared/components/EmptyState'
import { POINT_TYPE_META } from '../../shared/lib/pointMeta'
import type { TripPoint } from '../../shared/api/types'

export function AiPanel() {
  const { id } = useParams<{ id: string }>()
  const tripId = id as string
  const trip = useTrip(id)
  const plan = usePlanTrip(tripId)
  const apply = useApplyPlan(tripId)
  const recommendations = useRecommendations(id)

  const [prompt, setPrompt] = useState('')
  const [added, setAdded] = useState<TripPoint[] | null>(null)
  const [preview, setPreview] = useState<AiPlanPreview | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function onGenerate(e: FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      const generated = await plan.mutateAsync(prompt.trim() || trip.data?.destination || trip.data?.title || '')
      setPreview(generated)
      setAdded(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not generate a route')
    }
  }

  return (
    <div className="mx-auto max-w-2xl pb-12">
      <Link
        to={`/trips/${tripId}`}
        className="mb-4 inline-flex items-center gap-1 text-sm text-st-muted hover:text-st-text"
      >
        <ArrowLeft className="h-4 w-4" /> Back to overview
      </Link>

      <div className="mb-5 flex items-center gap-2">
        <span className="flex h-10 w-10 items-center justify-center rounded-st-md bg-st-primary/10 text-st-primary">
          <Sparkles className="h-5 w-5" />
        </span>
        <div>
          <h1 className="font-display text-2xl font-bold text-st-text">AI Planner</h1>
          <p className="text-sm text-st-muted">{trip.data?.title}</p>
        </div>
      </div>

      <form onSubmit={(e) => void onGenerate(e)} className="rounded-st-lg border border-st-line bg-st-surface p-4">
        <h2 className="mb-2 text-sm font-semibold text-st-text">Suggest places for your route</h2>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="A relaxed week in Rome with food, history, and a slow pace."
          rows={3}
          className="w-full rounded-st-md border border-st-line bg-st-surface p-3 text-sm text-st-text focus:border-st-primary focus:outline-none"
        />
        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="text-xs text-st-muted">The assistant drafts places you can edit or reorder.</p>
          <Button type="submit" variant="accent" disabled={plan.isPending}>
            {plan.isPending ? (
              <Spinner label="Generating…" />
            ) : (
              <>
                <Sparkles className="h-4 w-4" /> Generate
              </>
            )}
          </Button>
        </div>
        {error && <p className="mt-2 text-sm text-st-danger">{error}</p>}
      </form>

      {preview && <section className="mt-6 rounded-st-lg border border-st-line bg-st-surface p-4">
        <h2 className="mb-2 font-semibold">Preview — {preview.points.length} places</h2>
        <ul className="space-y-1 text-sm">{preview.points.map((p, i) =>
          <li key={`${p.title}-${i}`}>Day {p.day_index + 1}: {p.title}</li>)}</ul>
        <div className="mt-4 flex gap-2"><Button variant="accent" disabled={apply.isPending || preview.points.length === 0}
          onClick={async () => { try { setAdded(await apply.mutateAsync(preview)); setPreview(null); setPrompt('') }
            catch (err) { setError(err instanceof Error ? err.message : 'Could not apply route') } }}>Add to trip</Button>
          <Button variant="ghost" onClick={() => setPreview(null)}>Cancel</Button></div>
      </section>}

      {added && added.length > 0 && (
        <div className="mt-6">
          <h2 className="mb-2 text-sm font-semibold text-st-text">
            Added {added.length} {added.length === 1 ? 'place' : 'places'}
          </h2>
          <ul className="space-y-2">
            {added.map((p) => {
              const Icon = POINT_TYPE_META[p.type]?.Icon ?? MapPin
              return (
                <li
                  key={p.id}
                  className="flex items-center gap-3 rounded-st-md border border-st-line bg-st-surface px-3 py-2.5"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-st-sm bg-st-surface-2 text-st-primary">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-st-text">{p.title}</span>
                    <span className="text-xs text-st-muted">Day {p.day_index + 1}</span>
                  </span>
                </li>
              )
            })}
          </ul>
          <Link
            to={`/trips/${tripId}/timeline`}
            className="mt-3 inline-block text-sm font-medium text-st-primary hover:underline"
          >
            View on timeline →
          </Link>
        </div>
      )}

      <div className="mt-8">
        <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-st-text">
          <Lightbulb className="h-4 w-4 text-st-accent" /> Suggestions
        </h2>
        {recommendations.isLoading ? (
          <Spinner />
        ) : (recommendations.data ?? []).length === 0 ? (
          <EmptyState
            Icon={Lightbulb}
            title="No suggestions yet"
            description="Generate a route above, then refine it. Context-aware ideas will appear here."
          />
        ) : (
          <ul className="space-y-2">
            {(recommendations.data ?? []).map((r) => (
              <li key={r.id} className="rounded-st-md border border-st-line bg-st-surface p-3 text-sm">
                <p className="font-medium text-st-text">{r.suggestion}</p>
                {r.context && <p className="mt-0.5 text-xs text-st-muted">{r.context}</p>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
